import { toastr } from 'react-redux-toastr';
import * as http from './http';
import ApiService from './service';

const serviceEndpoint = 'user/v1';

export default class PaymentService extends ApiService {
  public async verifyPayment(
    referenceCode: string,
    retryPayment: boolean,
    referralAmount?: number
  ) {
    const url = `${this.apiDomain}/${serviceEndpoint}/student/verify-payment`;
    const referralAmountData = referralAmount || 0;
    return http.post(
      url,
      {
        paymentReference: referenceCode,
        retryPayment,
        referralAmount: referralAmountData,
        platform: 'web',
      },
      this.token
    );
  }
  public async getAdditionalFees() {
    const url = `${this.apiDomain}/${serviceEndpoint}/additional-fees`;
    return http.get(url, this.token);
  }
  public async getAdditionalFeeDetails(type: string, userId: string) {
    const url = `${this.apiDomain}/${serviceEndpoint}/additional-fees/${userId}/${type}`;
    return http.get(url, this.token);
  }
  public async initiatePayment(data: any) {
    let url = `${this.apiDomain}/${serviceEndpoint}/additional-fees`;
    if (data?.feeDetails?.isAbsenteeismFee) {
      url = `${this.apiDomain}/${serviceEndpoint}/additional-fees/initiate-absenteeism-payment`;
    }
    return http.post(url, data, this.token);
  }
  public async collectAdditionalFees(data: any, userId: string) {
    const url = `${this.apiDomain}/${serviceEndpoint}/additional-fees/${userId}`;
    return http.post(url, data, this.token);
  }

  public async verifyAdditionalFees(data: any, userId: string) {
    data.platform = 'web';
    let url = `${this.apiDomain}/${serviceEndpoint}/additional-fees/${userId}/verify-payment`;
    if (data?.isAbsenteeismFee) {
      url = `${this.apiDomain}/${serviceEndpoint}/additional-fees/absenteeism-addons/${userId}/verify-payment`;
    }
    return http.post(url, data, this.token);
  }
  public async verifyPaymentLink(data: any) {
    const url = `${this.apiDomain}/payment/v1/web-hook/get-payment-link`;
    return http.post(url, data);
  }
  public async verifyPaymentLinkApi(data: any) {
    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    try {
      const paymentLogObj = {
        paymentLink: data.paymentLink ?? '',
      };

      if (
        data.payType === 'additional-payment' ||
        data.payType === 'addon-payment'
      ) {
        return await http.post(
          `${this.apiDomain}/user/v1/additional-fees/check-payby-link/${data.userId}`,
          { paymentLogObj }
        );
      } else if (
        data.payType === 'quick-registration' ||
        data.payType === 'complete-payment'
      ) {
        return await http.post(
          `${this.apiDomain}/user/v2/check-payment-link-quick/${data.userId}`,
          { paymentLogObj }
        );
      } else if (
        data.payType === 'stage-payment' ||
        data.payType === 'registration' ||
        data.payType === 'transfer'
      ) {
        if (data.userId && !data.batchId) {
          return await http.post(
            `${this.apiDomain}/user/v2/check-payment-link/${data.userId}`,
            { paymentLogObj }
          );
        } else if (data.batchId && !data.userId) {
          return await http.post(
            `${this.apiDomain}/user/v2/check-batch-payment-link/${data.batchId}`,
            { paymentLogObj }
          );
        }
      } else if (data.payType === 'course-upgrade') {
        return await http.post(
          `${this.apiDomain}/user/v1/student/check-course-type-link-and-complete/${data.userId}`,
          { paymentLogObj }
        );
      } else if (
        data.payType === 'gear-change' ||
        data.payType === 'course-rule-change'
      ) {
        return await http.post(
          `${this.apiDomain}/user/v1/student/check-gear-type-link-and-complete/${data.userId}`,
          { paymentLogObj }
        );
      } else if (data.payType === 'course-package-change') {
        return await http.post(
          `${this.apiDomain}/user/v1/student/check-course-package-link-and-complete/${data.userId}`,
          { paymentLogObj }
        );
      } else if (data.payType === 'generic-service') {
        return await http.post(
          `${this.apiDomain}/user/v2/check-payment-link-genservice/${data.userId}`,
          { paymentLogObj }
        );
      }
    } catch (err) {
      toastr.error('Error', 'Error in payment link:');
    }
  }

  public async getEliteAdditionalFees() {
    const url = `${this.apiDomain}/${serviceEndpoint}/additional-fees/additional-addon-elite`;
    return http.get(url, this.token);
  }
  public async initiateEliteAdditionalPayment(data: any) {
    const url = `${this.apiDomain}/${serviceEndpoint}/additional-fees/initiate-elite-addon-payment`;
    return http.post(url, data, this.token);
  }
  public async verifyEliteAdditionalFees(data: any, userId: string) {
    data.platform = 'web';
    const url = `${this.apiDomain}/${serviceEndpoint}/additional-fees/elite-addons/${userId}/verify-payment`;
    return http.post(url, data, this.token);
  }

  public async verifyPaymentOnline(data: any) {
    const url = `${this.apiDomain}/payment/v1/web-hook/verify-payment-status`;
    return http.post(url, data);
  }
  public async verifyPaymentOnlineApi(data: any) {
    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    try {
      const paymentLogObj = {
        paymentReference: data.trackingId ?? '',
      };
      let payload = data?.payload;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch (e) {
          payload = {};
        }
      }
      const hasAddonConditions =
        !!payload &&
        (!!payload.type ||
          !!payload.addOnCode ||
          payload.isLumpsumAddon === true ||
          payload.isCancellationAddon === true ||
          payload.isEliteTestAddon === true ||
          payload.isLimitedLumpsumAddon === true);
      if (data.trackingId && hasAddonConditions && data.userId) {
        return await http.post(
          `${this.apiDomain}/user/v1/additional-fees/check-payby-link/${data.userId}`,
          { paymentLogObj }
        );
      } else {
        return data;
      }
    } catch (err) {
      toastr.error('Error', 'Error in payment link:');
    }
  }
}
